import type { ExportColumnObject } from '@/components/molecules/ExportDataDropDown/DownloadOptions';
import type { DomainDailyLiceCount } from '@/domain/lice';
import { formatMachineNumber } from '@/utils/localeFormatters';
import type { ParsedQuery } from '@/utils/queryParams';

/** Labels the export when the URL names no daypart. */
const DEFAULT_DAYPART_TAG = 'day';

const EXPORTED_DECIMALS = 2;

/** A row of the lice export. Every average is pre-formatted, so those columns are strings. */
interface LiceDownloadRow {
    date: string;
    daypartTag: string;
    count: number;
    countNoLice: number;
    femaleAvg: string;
    movingStageAvg: string;
    caligusElongatusAvg: string;
}

/**
 * Column paths as `useDownloadCSVXLSX` resolves them: `location.*` and `cage.*` come from the
 * cage context, `day.*` from the rows below.
 *
 * `location.timezone` and `location.sWeightPct` used to be declared here and are deliberately
 * gone. The download hook builds its row source field by field and has never carried either, so
 * both shipped as a permanently empty column in every lice export.
 */
function liceExportColumns(t: (key: string) => string, isChileanLice: boolean) {
    const columns: ExportColumnObject[] = [
        { name: 'location.name', header: t('common.labels.location') },
        { name: 'cage.name', header: t('common.labels.cage') },
        { name: 'day.date', header: t('common.labels.date') },
        { name: 'day.daypartTag', header: t('common.labels.daypart') },
        { name: 'day.count', header: t('cageLice.common.fishCount') },
        { name: 'day.countNoLice', header: t('cageLice.liceDownload.fishWithoutLice') },
        {
            name: 'day.femaleAvg',
            header: t(
                isChileanLice
                    ? 'cageLice.common.chileanFemaleLice'
                    : 'cageLice.common.matureFemaleLice'
            ),
        },
        { name: 'day.movingStageAvg', header: t('cageLice.common.mobileLice') },
    ];

    // Chilean-lice locations do not track Caligus, so the field is absent from every row there
    // and the column would only ever be blank.
    if (!isChileanLice) {
        columns.push({ name: 'day.caligusElongatusAvg', header: t('cageLice.common.caligus') });
    }

    return columns;
}

export interface LiceDownloadParams {
    rows: DomainDailyLiceCount[];
    /** The `?d=` query param, which is what the page is actually showing; undefined without one. */
    daypart: ParsedQuery<string>[string] | undefined;
    /** Translator for the column headers. */
    t: (key: string) => string;
    /** Chile does not track Caligus, and names the adult female count differently. */
    isChileanLice: boolean;
}

export interface LiceDownload {
    data: LiceDownloadRow[];
    columns: ExportColumnObject[];
    /**
     * Passed to the download hook, which prefixes it with the location and pen names. Follows
     * the Behavior export so a folder of exports from different modules sorts together.
     */
    nameToInclude: string;
}

/**
 * The lice CSV/XLSX export, shared by the dashboard and the graphs page.
 *
 * Both pages carried a byte-identical private copy that declared its columns as bare paths, so
 * the spreadsheet's header row read `femaleAvg`, `countNoLice`, `location.name`. Headers are
 * translated here instead, the way the Behavior export does it — the shared dropdown already
 * accepts `{ name, header }`, so nothing downstream changes.
 *
 * The daypart is passed in rather than the whole query-params tuple: a download helper has no
 * business reading the URL, and the copies took it as `any` and then dereferenced it without a
 * guard despite declaring it optional.
 */
export function buildLiceDownload(params: LiceDownloadParams): LiceDownload {
    const { rows, daypart, t, isChileanLice } = params;

    const daypartTag = (Array.isArray(daypart) ? daypart[0] : daypart) || DEFAULT_DAYPART_TAG;

    const data = (rows ?? []).map((row) => ({
        date: row.day,
        daypartTag,
        count: row.count,
        countNoLice: row.countNoLice,
        femaleAvg: formatMachineNumber(row.femaleAvg, EXPORTED_DECIMALS),
        movingStageAvg: formatMachineNumber(row.movingStageAvg, EXPORTED_DECIMALS),
        // Absent at Chilean-lice locations. The copies called `.toFixed` on it unguarded, so
        // exporting one of those cages threw.
        caligusElongatusAvg: formatMachineNumber(row.caligusElongatusAvg, EXPORTED_DECIMALS) ?? '',
    }));

    // The newest day in the export, so a file names the period it covers. Rows arrive ascending.
    const lastDay = data.at(-1)?.date;

    return {
        data,
        columns: liceExportColumns(t, isChileanLice),
        nameToInclude: ['Optoscale-Lice-Data', lastDay].filter(Boolean).join('-'),
    };
}
